import { useState, useCallback, useRef, useEffect } from 'react';
import { updateTrigger } from '../services';

const AUTOSAVE_DELAY = 1000;

export const useAutosave = (triggerData, onSuccess, onError, onDataSaved = null) => {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const debounceTimerRef = useRef(null);
  const lastSavedDataRef = useRef(null);
  const pendingChangesRef = useRef({});

  // Main autosave function
  const performAutosave = useCallback(async (changes) => {
    if (!triggerData?.uuid || !changes) return;

    setIsSaving(true);
    setError(null);

    try {
      const payload = {
        ...triggerData,
        ...changes,
        lastAutosave: new Date().toISOString()
      };

      const result = await updateTrigger(payload);

      if (result.success) {
        setLastSaved(new Date());
        setHasUnsavedChanges(false);
        lastSavedDataRef.current = { ...triggerData, ...changes };
        pendingChangesRef.current = {};
        
        // Notify parent component about saved data
        if (onDataSaved) {
          onDataSaved({ ...triggerData, ...changes });
        }
        
        if (onSuccess) {
          onSuccess(result.data.trigger);
        }
      } else {
        throw new Error(result.message || 'Error saving');
      }
    } catch (err) {
      setError(err.message);
      setHasUnsavedChanges(true);
      
      if (onError) {
        onError(err.message);
      }
    } finally {
      setIsSaving(false);
    }
  }, [triggerData, onSuccess, onError, onDataSaved]);

  // Function to register changes with debounce
  const registerChange = useCallback((fieldName, value) => {
    pendingChangesRef.current[fieldName] = value;
    setHasUnsavedChanges(true);

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // New timer with debounce
    debounceTimerRef.current = setTimeout(() => {
      const changes = pendingChangesRef.current;
      if (Object.keys(changes).length > 0) {
        performAutosave(changes);
      }
    }, AUTOSAVE_DELAY);
  }, [triggerData, performAutosave]);

  // Function to save immediately (without debounce)
  const saveImmediately = useCallback((changes = null) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const changesToSave = changes || pendingChangesRef.current;
    if (Object.keys(changesToSave).length > 0) {
      performAutosave(changesToSave);
    }
  }, [performAutosave]);

  // Retry function
  const retrySave = useCallback(() => {
    if (Object.keys(pendingChangesRef.current).length > 0) {
      performAutosave(pendingChangesRef.current);
    }
  }, [performAutosave]);

  // Initialize with trigger data
  useEffect(() => {
    if (triggerData?.uuid) {
      lastSavedDataRef.current = triggerData;
    }
  }, [triggerData?.uuid]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    registerChange,
    saveImmediately,
    retrySave,
    isSaving,
    lastSaved,
    error,
    hasUnsavedChanges,
    pendingChanges: pendingChangesRef.current
  };
}; 